import type {FL} from "../types/esri-extensions.tsx";
import {
    type ActionConfig,
    buildCountQuery,
    buildQuery, configureActions,
    getLayerFromMapByPortalIdAndSetInitState,
    queryAllFeatures
} from "../utils/MapUtilities.tsx";
import type FeatureSet from "@arcgis/core/rest/support/FeatureSet";


export class LayerGroupService {
    // Define the properties of the LayerGroupService class
    constructor(map: __esri.WebMap,  groupType: 'treatments' | 'activities') {
        this.map = map;
        this.groupType = groupType;
    }

    groupType: 'treatments' | 'activities';
    map: __esri.WebMap;
    pointLayer!: FL;
    lineLayer!: FL | null;
    polygonLayer!: FL;


    async queryLayers(where: string, outFields: string[], geometry: __esri.Geometry | null, pointNum: number, lineNum: number, polyNum: number, startIndex: number, sortByValue: string,  featureAbortController: AbortController): Promise<[] | FeatureSet[]> {
        this.checkLayersPresent();
        const promises = [];
        promises.push(queryAllFeatures(buildQuery(this.pointLayer, where, outFields, geometry, pointNum, startIndex, sortByValue), this.pointLayer, sortByValue, featureAbortController));
        promises.push(queryAllFeatures(buildQuery(this.polygonLayer, where, outFields, geometry, polyNum, startIndex, sortByValue), this.polygonLayer, sortByValue,  featureAbortController));

        // activities do not include lines, but treatments do
        if (this.groupType === "treatments" && this.lineLayer) {
            promises.push(queryAllFeatures(buildQuery(this.lineLayer, where, outFields, geometry, lineNum, startIndex, sortByValue), this.lineLayer, sortByValue,  featureAbortController));
        }

        return Promise.all(promises);
    }

    checkLayersPresent(): void {
        if (this.groupType == 'treatments' && !this.lineLayer) {
            throw new Error("Line layer is required for treatments group type");
        }
        if (!this.pointLayer || !this.polygonLayer) {
            throw new Error("point and polygon layers are required for treatments group type");

        }
    }

    async queryFeatureCount(where: string, geometry: __esri.Geometry | null, featureAbortController: AbortController): Promise<number[]> {
        this.checkLayersPresent();
        const promises = [];
        promises.push(this.pointLayer.queryFeatureCount(buildCountQuery(this.pointLayer, where, geometry ), {signal: featureAbortController.signal}));
        promises.push(this.polygonLayer.queryFeatureCount(buildCountQuery(this.polygonLayer, where, geometry), {signal: featureAbortController.signal}));

        // activities do not include lines, but treatments do
        if (this.groupType === "treatments" && this.lineLayer) {
            promises.push(this.lineLayer.queryFeatureCount(buildCountQuery(this.lineLayer, where,geometry,), {signal: featureAbortController.signal}));
        }

        return Promise.all(promises);
    }

   async configureLayers(pointsId: string, linesId: string | null, polysId: string) {
        const webmap = this.map as __esri.WebMap;
        this.pointLayer = await getLayerFromMapByPortalIdAndSetInitState(webmap, pointsId) as FL;
        this.polygonLayer = await getLayerFromMapByPortalIdAndSetInitState(webmap, polysId) as FL;
        if (linesId && this.groupType === 'treatments') {
            this.lineLayer = await getLayerFromMapByPortalIdAndSetInitState(webmap, linesId) as FL;
        }
    }

    async configureLayerActions(pointActionConfigs: ActionConfig[], lineActionConfigs: ActionConfig[], polyActionConfigs: ActionConfig[]) {
       configureActions(this.pointLayer, pointActionConfigs);
       if(this.lineLayer){
           configureActions(this.lineLayer, lineActionConfigs);
       }
       configureActions(this.polygonLayer, polyActionConfigs);

    }

    refreshLayers() {
        this.pointLayer.refresh();
        this.polygonLayer.refresh();
        if (this.lineLayer) {
            this.lineLayer.refresh();
        }
    }



}